import { Pill } from '@unifyapps/ui/components/Pill';
import type { TypographyVariants, TypographyWeight } from '@unifyapps/ui/components/Typography';
import { clsx } from 'clsx';
import { memo } from 'react';
import { isPlatformSideNav } from '../../helpers';
import type { NavigationItemSlotPropsType, NotificationActionPropsType } from '../../types';
import BaseActionNavItem from './BaseActionNavItem';

type Props = {
  id: string;
  isActive?: boolean;
  icon?: string;
  iconClassName?: string;
  activeIcon?: string;
  endIcon?: string;
  activeEndIcon?: string;
  name: string;
  onClick: (args: { onOpen: () => void; onClose: () => void }) => void;
  variant: 'lite' | 'rich';
  onOpenChange?: (id: string, open: boolean) => void;
  slotProps?: NavigationItemSlotPropsType;
  actionProps?: NotificationActionPropsType;
  action?: string;
  disabled?: boolean;
  shortcutKeys?: string;
  renderEndDecorator?: () => React.ReactNode;
};

const LABEL_TYPOGRAPHY = {
  variant: 'text-xs' as TypographyVariants,
  weight: 'medium' as TypographyWeight,
  maxNumberOfLines: 0,
};

function ActionNavigationItem({
  isActive = false,
  activeIcon,
  endIcon,
  activeEndIcon,
  icon,
  name,
  onClick,
  variant,
  onOpenChange,
  id,
  slotProps,
  actionProps,
  disabled,
  shortcutKeys,
  renderEndDecorator,
  iconClassName,
}: Props) {
  const count = actionProps?.unopenedNotificationsCount ?? 0;
  const _isPlatformSideNav = isPlatformSideNav(slotProps?.navigationItem?.labelVariant);

  return (
    <BaseActionNavItem
      actionProps={actionProps}
      activeEndIcon={activeEndIcon}
      activeIcon={activeIcon}
      disabled={disabled}
      endIcon={endIcon}
      icon={icon}
      iconClassName={iconClassName}
      id={id}
      isActive={isActive}
      name={name}
      onClick={onClick}
      onOpenChange={onOpenChange}
      renderEndDecorator={renderEndDecorator}
      shortcutKeys={shortcutKeys}
      slotProps={slotProps}
      variant={variant}
    >
      {/* generic count badge for any action item that supplies a count via actionProps —
          notifications' unopened count and the builder DevTools' buffered-error count both use it */}
      {count > 0 ? (
        <BaseActionNavItem.Slot name="badge">
          <Pill
            className={
              _isPlatformSideNav
                ? 'min-w-4'
                : clsx('px-xs py-xxs min-w-5', { '-mt-0.5': count > 99 })
            }
            color="error"
            label={count > 99 ? '99+' : String(count)}
            labelTypography={_isPlatformSideNav ? undefined : LABEL_TYPOGRAPHY}
            shape="rounded"
            size={_isPlatformSideNav ? 'xxs' : 'xs'}
            variant="solid"
          />
        </BaseActionNavItem.Slot>
      ) : null}
    </BaseActionNavItem>
  );
}

const ActionNavigationItemMemo = memo(ActionNavigationItem);

export { ActionNavigationItemMemo as ActionNavigationItem };
